<template>
  <div>
    <nav class="bg-gray-900 fixed w-full z-20 top-0 left-0 border-b border-gray-200 dark:border-gray-600 h-[50px]">
      <div class="max-w-screen-xl flex items-center justify-between mx-auto p-3">
        <!-- Title -->
        <div class="flex items-center text-white mr-6">
          <span class="esign-title md:text-lg text-sm text-gray-100 whitespace-nowrap">Please review & sign</span>
        </div>

        <!-- Actions -->
        <div class="flex items-center space-x-2">

          <v-btn
            v-if="envelopeStatus !== 'completed'"
            :disabled="acceptButtonDisabled"
            :loading="envelopeLoading.accepting"
            class="whitespace-nowrap"
            color="primary"
            size="small"
            theme="dark"
            @click="openConfirmSignDialog"
          >
            Accept {{ isSample ? 'Sample' : '' }}
          </v-btn>

          <v-menu v-if="envelopeStatus !== 'completed'">
            <template v-slot:activator="{ props }">
              <v-btn
                class="whitespace-nowrap ml-2"
                color="primary"
                size="small"
                theme="dark"
                v-bind="props"
                variant="text"
              >
                <span class="d-sm-block d-none">Other Actions</span>
                <v-icon class="mb-1 ml-1 d-sm-block d-none">mdi-chevron-down</v-icon>
                <v-icon class="d-sm-none d-block" size="small">mdi-menu</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :disabled="!!envelopeCustomerCompleted" @click="handleSaveProcess()">
                <template v-slot:prepend>
                  <v-icon>mdi-content-save</v-icon>
                </template>

                <v-list-item-title>Save Progress</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!!envelopeCustomerCompleted" @click="declineToSign()">
                <template v-slot:prepend>
                  <v-icon color="red">mdi-close-circle</v-icon>
                </template>

                <v-list-item-title>Decline to Sign</v-list-item-title>

              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </nav>
    <div class="w-full border-bottom fixed bg-gray-100" style="top: 50px; text-align: center;height: 30px; z-index: 10">
      <div class="flex justify-content-center h-full " style="position: relative;display: inline-block;">
        <span v-if="!envelopeCustomerCompleted">Fields remaining: {{ esignSessionData.remainingFieldsCount }}</span>
        <span v-else>Completed: {{ envelopeCustomerCompleted }}</span>
      </div>
    </div>
  </div>

  <v-dialog v-model="confirmAndSignDialog" max-width="400">
    <v-card>
      <v-card-title class="text-h5">Almost there</v-card-title>
      <v-divider opacity="50"/>
      <v-card-text>
        I agree to be legally bound by this document and the
        Fleetwire Terms of Service. Click on 'I Agree' to sign this document.

        <v-alert v-if="isSample" type="warning">
          Sample: <br><br>
          Here the signer would agree and the envelope will be signed & sealed and sent to each party.
        </v-alert>
      </v-card-text>
      <v-divider opacity="50"/>
      <v-card-actions>
        <v-spacer/>
        <v-btn :disabled="envelopeLoading.accepting" color="red" size="small" variant="text" @click="confirmAndSignDialog = false">
          I Disagree
        </v-btn>
        <v-btn :disabled="isSample && !debugDrawerOpen" :loading="envelopeLoading.accepting" color="green text-white" @click="handleAcceptAndSign();">
          I Agree
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-snackbar v-model="isProgressSaved">
    Progress saved successfully!
    <template v-slot:actions>
      <v-btn variant="text" @click="isProgressSaved = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script lang="ts" setup>
import {computed, ref} from 'vue';
import {useEnvelope} from '@/composables/useEnvelope';
import {useRoute, useRouter} from 'vue-router';
import {useAppSettings} from '@/composables/useAppSettings';

const route = useRoute();
const router = useRouter();
const {
  envelopeLoading,
  envelopeStatus,
  esignSessionData,
  esignEnvelopeDataCopy,
  envelopeCustomerCompleted,
  envelopeIsReady,
  acceptAndSignEnvelope,
  saveProgress
} = useEnvelope();
const {debugDrawerOpen} = useAppSettings();
const isProgressSaved = ref(null);
const confirmAndSignDialog = ref(null);

const recipientKey = route.query.key as string;

const acceptButtonDisabled = computed(() =>
  !envelopeIsReady.value || !!esignSessionData.value.remainingFieldsCount
);

const getEnvelopeData = () =>
  esignEnvelopeDataCopy.value.flatMap((page) => page.pageData);


const isSample = computed(() => {
  return route.name === 'esign.recipient.v2.sample';
});

const handleSaveProcess = async () => {
  await saveProgress({
    uuid: esignSessionData.value.envelopeId,
    recipientId: esignSessionData.value.recipientId,
    allSigned: null,
    ip: esignSessionData.value.ip,
    data: getEnvelopeData(),
    sample: isSample.value,
  }).then(() => {
    console.dir('Save progress success');
    isProgressSaved.value = true;
  });
};


const handleAcceptAndSign = async () => {
  if (esignSessionData.value.remainingFieldsCount === 0) {
    await acceptAndSignEnvelope({
      uuid: esignSessionData.value.envelopeId,
      recipientId: esignSessionData.value.recipientId,
      allSigned: null,
      ip: esignSessionData.value.ip,
      data: getEnvelopeData(),
      sample: isSample.value,
      recipientKey
    }).then(() => {
      console.dir('Save progress success');

      setTimeout(() => {
        router.push({name: 'esign.recipient.complete'}).catch(() => {
        });
      }, 1000);
    });
  }
};

const openConfirmSignDialog = async () => {
  confirmAndSignDialog.value = true;
};

const declineToSign = () => {
  console.dir('declineToSign');
};
</script>
